import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';

const PREDEFINED_RANGES = [
  { label: 'Last 7 Days', value: 7 },
  { label: 'Last 30 Days', value: 30 },
  { label: 'Last 90 Days', value: 90 },
  { label: 'Last 6 Months', value: 180 },
  { label: 'Last Year', value: 365 },
  { label: 'Custom Range', value: 'custom' },
];

/** Latest calendar day included in analytics (today is excluded until ETL completes). */
const latestSelectableEnd = () => moment().subtract(1, 'day').endOf('day');

const DateRangeSelector = ({ onRangeChange, defaultDays = 7, value }) => {
  // Determine selected range based on value prop or default
  const getSelectedRange = () => {
    if (!value || !value.startDate || !value.endDate) {
      return defaultDays.toString();
    }
    
    const daysDiff = moment(value.endDate).diff(moment(value.startDate), 'days') + 1;
    const predefinedRange = PREDEFINED_RANGES.find(r => r.value === daysDiff);
    
    if (predefinedRange && moment(value.endDate).isSame(latestSelectableEnd(), 'day')) {
      return predefinedRange.value.toString();
    }
    
    return 'custom';
  };

  const [selectedRange, setSelectedRange] = useState(getSelectedRange());
  const [customStartDate, setCustomStartDate] = useState(value?.startDate || null);
  const [customEndDate, setCustomEndDate] = useState(value?.endDate || null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Update local state when value prop changes
  React.useEffect(() => {
    if (value && value.startDate && value.endDate) {
      const daysDiff = moment(value.endDate).diff(moment(value.startDate), 'days') + 1;
      const predefinedRange = PREDEFINED_RANGES.find(r => r.value === daysDiff);
      
      let newSelectedRange;
      if (predefinedRange && moment(value.endDate).isSame(latestSelectableEnd(), 'day')) {
        newSelectedRange = predefinedRange.value.toString();
      } else {
        newSelectedRange = 'custom';
      }
      
      setSelectedRange(newSelectedRange);
      setCustomStartDate(value.startDate);
      setCustomEndDate(value.endDate);
    }
  }, [value]);

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  const handlePredefinedRange = (days) => {
    setSelectedRange(days.toString());
    setDropdownOpen(false);

    if (days === 'custom') {
      // Don't change dates, just set to custom mode
      return;
    }

    const endDate = latestSelectableEnd();
    const startDate = endDate.clone().subtract(days - 1, 'days').startOf('day');

    const newRange = {
      startDate: startDate.toDate(),
      endDate: endDate.toDate(),
      startTimestamp: startDate.valueOf(),
      endTimestamp: endDate.valueOf(),
    };

    setCustomStartDate(newRange.startDate);
    setCustomEndDate(newRange.endDate);
    onRangeChange(newRange);
  };

  const handleCustomDateChange = (dates) => {
    const [start, end] = dates;
    setCustomStartDate(start);
    setCustomEndDate(end);

    if (start && end) {
      setSelectedRange('custom');

      const startMoment = moment(start).startOf('day');
      const endMoment = moment(end).endOf('day');

      onRangeChange({
        startDate: startMoment.toDate(),
        endDate: endMoment.toDate(),
        startTimestamp: startMoment.valueOf(),
        endTimestamp: endMoment.valueOf(),
      });
    }
  };

  const getSelectedLabel = () => {
    const range = PREDEFINED_RANGES.find(r => r.value.toString() === selectedRange);
    return range ? range.label : 'Custom Range';
  };

  const getDateRangeDisplay = () => {
    if (customStartDate && customEndDate) {
      return `${moment(customStartDate).format('MMM DD, YYYY')} - ${moment(customEndDate).format('MMM DD, YYYY')}`;
    }
    return 'Select dates';
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem' }}>
      {/* Dropdown for predefined ranges */}
      <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
        <DropdownToggle
          caret
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.875rem',
            backgroundColor: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            color: '#374151',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          {getSelectedLabel()}
        </DropdownToggle>
        <DropdownMenu>
          {PREDEFINED_RANGES.map((range) => (
            <DropdownItem
              key={range.value}
              onClick={() => handlePredefinedRange(range.value)}
              active={selectedRange === range.value.toString()}
            >
              {range.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>

      {/* Always visible date range picker */}
      <div style={{ 
        display: 'inline-block',
        position: 'relative'
      }}>
        <DatePicker
          selected={customStartDate}
          onChange={handleCustomDateChange}
          startDate={customStartDate}
          endDate={customEndDate}
          selectsRange
          dateFormat="MMM dd, yyyy"
          placeholderText="Select date range"
          maxDate={latestSelectableEnd().toDate()}
          className="form-control"
          style={{ 
            padding: '0.5rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem',
            border: '1px solid #d1d5db',
            minWidth: '240px',
            cursor: 'pointer'
          }}
          customInput={
            <input
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                borderRadius: '0.375rem',
                border: '1px solid #d1d5db',
                minWidth: '260px',
                cursor: 'pointer',
                backgroundColor: '#fff'
              }}
              value={getDateRangeDisplay()}
              readOnly
            />
          }
        />
      </div>
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(DateRangeSelector, (prevProps, nextProps) => {
  return (
    prevProps.defaultDays === nextProps.defaultDays &&
    prevProps.value?.startTimestamp === nextProps.value?.startTimestamp &&
    prevProps.value?.endTimestamp === nextProps.value?.endTimestamp &&
    prevProps.onRangeChange === nextProps.onRangeChange
  );
});
